import Phaser from "phaser";
import "./styles/main.css";
import { GAME } from "@/config";
import { CARE_TIERS } from "@/data/tunables";
import { PALETTE_HEX } from "@/palette";
import { runtime } from "@/systems/runtime";
import { clearSave } from "@/saves/SaveManager";
import { PreloadScene } from "@/scenes/PreloadScene";
import { OfficeScene } from "@/scenes/OfficeScene";
import { TierScene } from "@/scenes/TierScene";
import { AlumniScene } from "@/scenes/AlumniScene";
import { consentGiven, hideEventCard, hideInsuranceVeil, mountOverlay, showConsentForm, showEventCard, showFatalPanel, showInsuranceVeil, showOfflineBanner, showSatireToast, showSaveWarning, showShutdownScreen, shownEventCardId, type SceneKey } from "@/ui/dom";
import { ensurePack, packForScene, packReady } from "@/sprites/artPacks";
import { startTelemetry } from "@/systems/telemetry";
import { answerCard } from "@/systems/actions";
import { cardById } from "@/data/cards";
import { EVENTS } from "@/data/tunables";

/**
 * Boot order (v0.2 RFC §8): runtime first (SaveError → fatal panel, no game),
 * then the Phaser shell (office + five tier scenes + alumni), then the DOM
 * overlay wired to scene.run/sleep tab switching.
 */

runtime.boot();

if (runtime.loadError) {
  showFatalPanel(runtime.loadError.message, () => {
    runtime.stop(); // no tab-hide save may outlive the wipe (same as Menu's New Game)
    clearSave();
    window.location.reload();
  });
} else {
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: "game-canvas",
    backgroundColor: PALETTE_HEX.cream,
    width: GAME.width,
    height: GAME.height,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
  });

  // Dev/e2e introspection handle (harmless in prod; no gameplay surface).
  if (import.meta.env.DEV) {
    // Dev/e2e introspection only — runtime.act IS the gameplay surface, so
    // these must never ship in a production bundle (review finding, v0.9).
    (window as unknown as { __game?: Phaser.Game }).__game = game;
    (window as unknown as { __runtime?: typeof runtime }).__runtime = runtime;
    // v0.10 The Control Room (dev instrument): ?ctrl feeds every economy
    // machine to the Stately inspector; ?autoplay drives a greedy run to
    // watch. Dynamic imports keep both out of any prod bundle path.
    const params = new URLSearchParams(window.location.search);
    if (params.has("ctrl")) {
      void import("./dev/controlRoom").then((m) => m.startControlRoom());
    }
    if (params.has("autoplay")) {
      void import("./dev/autoplay").then((m) => m.startAutoplay());
    }
  }

  let currentScene: SceneKey = "office";
  // Nav is inert until PreloadScene hands off to the office (v0.7 review, Codex
  // P2): a saved/prestige run renders the edge arrows while art still loads, and
  // a tap during that window would desync `currentScene` against the scene the
  // preloader unconditionally starts, wedging later navigation.
  let navReady = false;
  const overlay = mountOverlay(onScene);
  // v0.12.1: ask the browser to protect the save from storage eviction
  // (iOS Safari clears site data after ~7 days away for non-installed
  // sites). Best-effort — a denial changes nothing, the Menu line tells
  // iOS players the Add-to-Home-Screen escape hatch.
  void navigator.storage?.persist?.()?.catch(() => {}); // ?. before .catch too — the chain can yield undefined (bot leg)
  // v0.12 telemetry: silent no-op without the build-time env (dev/e2e).
  startTelemetry(() => runtime.state, {
    fresh: runtime.wasFresh,
    offlineTicks: runtime.offlineReport?.ticks ?? 0,
  });
  runtime.onSaveFailure = showSaveWarning;
  // 0.12.6 (bot leg): umami loads ONLY in prod and ONLY once the consent
  // form is signed — the paperwork precedes the reporting, dev/staging
  // never even fetch the script (data-domains guards tracking besides).
  const loadUmami = (): void => {
    if (!import.meta.env.PROD) return;
    const s = document.createElement("script");
    s.defer = true;
    s.src = "https://analytics.russalo.com/script.js";
    s.dataset.websiteId = "b2283b63-f756-45c1-8777-cdef7d8f2305";
    s.dataset.domains = "play.rehabtycoon.com";
    document.head.appendChild(s);
  };
  // 0.12.2: the playtest consent form — once per browser, over everything.
  if (!consentGiven()) showConsentForm(loadUmami);
  else loadUmami();

  // A meter game-over (v0.5) forces a prestige inside the tick; surface the
  // shutdown screen globally (the scenes handle their own event animations).
  runtime.subscribe((events) => {
    const over = events.find((e) => e.type === "gameOver");
    if (over?.type === "gameOver") showShutdownScreen(over.reason, over.payout);
    // v0.13: satire toasts are global DOM (scene-independent by design).
    for (const e of events) {
      if (e.type === "satire") showSatireToast(e.text);
    }
    reconcileEventCard(); // v0.14: surface / clear the front-and-center card
  });

  // v0.14: one card on screen at a time; show the FIRST open card, clear it
  // when it's gone (answered or expired), then let the next queued one surface.
  function reconcileEventCard(): void {
    const open = runtime.state.run.events.cards;
    const shown = shownEventCardId();
    if (open.length === 0) {
      if (shown !== null) hideEventCard();
      return;
    }
    if (shown !== null && open.some((c) => c.id === shown)) return; // still valid
    const next = open[0]!;
    const def = cardById(next.defId);
    showEventCard(
      {
        id: next.id,
        title: def.title,
        body: def.body,
        crisis: def.crisis === true,
        choices: def.choices.map((c) => c.label),
        hero: `assets/cards/${def.id}.webp`, // v0.14.1 hero banner (graceful if absent)
        lifetimeTicks: EVENTS.card.lifetimeTicks,
        getTicksLeft: () => runtime.state.run.events.cards.find((c) => c.id === next.id)?.ticksLeft ?? 0,
      },
      (choiceIndex) => {
        try {
          runtime.act(answerCard(runtime.state, next.id, choiceIndex));
        } catch (err) {
          console.warn("card answer raced its expiry:", err);
        }
        hideEventCard();
        reconcileEventCard();
      },
    );
  }

  // PreloadScene loads real sprite packs (runtime art layer) then starts the
  // office; absent/failed art falls back to the baked placeholders.
  game.scene.add("preload", PreloadScene, false);
  game.scene.add("office", OfficeScene, false);
  for (const tier of CARE_TIERS) {
    game.scene.add(`tier-${tier}`, new TierScene(tier), false);
  }
  game.scene.add("alumni", AlumniScene, false);
  game.scene.start("preload", { overlay, onReady: () => (navReady = true) });

  // v0.11: a tab whose art pack hasn't warmed waits behind the insurance
  // veil; the LATEST tap wins if the player navigates again mid-load.
  let pendingScene: SceneKey | null = null;

  function switchTo(key: SceneKey): void {
    game.scene.run(key, { overlay });
    game.scene.sleep(currentScene);
    currentScene = key;
    overlay.setActiveTab(key);
  }

  function onScene(key: SceneKey): void {
    if (!navReady) return;
    if (key === currentScene) {
      // Re-tapping the CURRENT tab while another tier is loading is a
      // bail-out — cancel the pending switch and drop the veil (v0.11
      // review: this case previously stranded the veil AND force-
      // navigated the player when the load finished).
      pendingScene = null;
      hideInsuranceVeil();
      return;
    }
    const pack = packForScene(key);
    if (packReady(pack)) {
      pendingScene = null;
      hideInsuranceVeil();
      switchTo(key);
      return;
    }
    pendingScene = key;
    showInsuranceVeil();
    void ensurePack(pack)
      .catch((err) => {
        // ensurePack's contract is always-resolve; this guards a future
        // regression — the veil must NEVER strand (placeholders instead).
        console.error(`[art] pack "${pack}" load rejected — opening with placeholders:`, err);
      })
      .then(() => {
        if (pendingScene !== key) return; // superseded by a later tap
        pendingScene = null;
        hideInsuranceVeil();
        switchTo(key);
      });
  }

  if (runtime.offlineReport) {
    // An offline meter game-over supersedes the normal away-banner — the run
    // ended while away, so show the shutdown screen instead (v0.5).
    if (runtime.offlineReport.gameOver) {
      showShutdownScreen(runtime.offlineReport.gameOver.reason, runtime.offlineReport.gameOver.payout);
    } else {
      showOfflineBanner(runtime.offlineReport);
    }
  }

  const refit = () => {
    game.scale.refresh();
    overlay.relayoutHero();
  };
  window.addEventListener("resize", refit);
  window.visualViewport?.addEventListener("resize", refit);
  window.setTimeout(refit, 300);
  window.setTimeout(refit, 1500);
}
